import { useMutation, useQueryClient } from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { providerMutationKeys, providerQueryKeys } from './query-keys';

/**
 * Hook to delete a provider.
 * Waits until the read model no longer shows the provider, then invalidates the cache.
 */
export function useDeleteProvider(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: providerMutationKeys.deleteProvider(tenantId),
    mutationFn: async (providerId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      await providerService.deleteProvider(tenantId, providerId);
      await pollForResourceGone(() =>
        providerService.findProvider(tenantId, providerId)
      );
      return providerId;
    },
    onSuccess: (_, providerId) => {
      qc.invalidateQueries({
        queryKey: providerQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
      qc.removeQueries({
        queryKey: providerQueryKeys.detail(tenantId, providerId),
      });
    },
  });
}
